import { Component, EventEmitter, inject, Input, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { TranslatePipe } from "../../pipe/translation.filter.pipe";
import { TableModule } from "primeng/table";
import { ApeButtonComponent } from "../ape-button/ape-button.component";
import { ApeStatusBadgeComponent } from "../ape-status-badge/ape-status-badge.component";
import { TRANSLATION_KEYS } from "../../constants/translations/translation-keys";
import { MonitoringUserSummaryExtended, MonitoringUserSummaryIdsExtended } from "src/app/services/api/monitoring-backoffice-api.service";
import { ApeAvatarComponent } from "../ape-avatar/ape-avatar.component";
import { Router } from "@angular/router";
import { LoggerService } from "src/ancestors/logger.service";
import { ApeMenuComponent } from "../ape-menu/ape-menu.component";
import { MenuItem } from "primeng/api";
import { Menu } from "primeng/menu";
import { TranslateService } from "src/app/util/translate.service";

@Component({
  selector: "ape-admin-monitoring-users-table",
  templateUrl: "./ape-admin-monitoring-users-table.component.html",
  standalone: true,
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TranslatePipe,
    TableModule,
    ApeButtonComponent,
    ApeStatusBadgeComponent,
    ApeAvatarComponent,
    ApeMenuComponent
  ]
})
export class ApeAdminMonitoringUsersTableComponent {
  /**
   * Chiavi di traduzione per type safety
   */
  readonly TRANSLATION_KEYS = TRANSLATION_KEYS;

  private logger = inject(LoggerService);

  @Input() usersData: MonitoringUserSummaryExtended[] = [];

  @Input() allUsersChecked: boolean = false;

  @Input() allUsersData: MonitoringUserSummaryIdsExtended[] = [];

  /**
   * Riferimento al menu tiered
   */
  @ViewChild("apeMenu") apeMenu!: Menu;

  /**
   * Menu items per le azioni
   */
  menuItems: MenuItem[] = [];

  @Input() processId: string | null = null;

  @Output() accessProcess = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onAllUsersCheckedChange = new EventEmitter<boolean>();

  @Output() onReopenEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onReopenSelfEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSolicitSelfEvaluation = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() impersonateUser = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() impersonateManager = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onDownloadEvaluationCard = new EventEmitter<MonitoringUserSummaryExtended>();

  @Output() onSendCalibrationCards = new EventEmitter<MonitoringUserSummaryExtended>();

  private router = inject(Router);

  private translateService = inject(TranslateService);

  getStatusKind(status: string) {
    switch (status) {
      case "TODO":
        return "in-progress";
      case "COMPLETED":
        return "completed";
      case "NOT_DONE":
        return "disable";
    }
    return "disable";
  }

  allUsersCheckedChange(): void {
    this.allUsersChecked = !this.allUsersChecked;
    this.onAllUsersCheckedChange.emit(this.allUsersChecked);
  }

  protected reopenEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!user.canReopenEvaluation) return;
    this.logger.info(`Reopen evaluation for user ${user.userId}`);
    this.onReopenEvaluation.emit(user);
  }

  protected reopenSelfEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!user.canReopenSelfEvaluation) return;
    this.logger.info(`Reopen self evaluation for user ${user.userId}`);
    this.onReopenSelfEvaluation.emit(user);
  }

  private canSolicitEvaluation(user: MonitoringUserSummaryExtended) {
    return user?.canSolicitEvaluation ?? false;
  }

  private canSolicitSelfEvaluation(user: MonitoringUserSummaryExtended) {
    return user?.canSolicitSelfEvaluation ?? false;
  }

  protected solicitEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!this.canSolicitEvaluation(user)) return;
    this.logger.info(`Solicit evaluation for user ${user.userId}`);
    this.onSolicitEvaluation.emit(user);
  }

  protected solicitSelfEvaluation(user: MonitoringUserSummaryExtended) {
    if (!user) return;
    if (!this.canSolicitSelfEvaluation(user)) return;
    this.logger.info(`Solicit self evaluation for user ${user.userId}`);
    this.onSolicitSelfEvaluation.emit(user);
  }

  toggleActionColumn(user: MonitoringUserSummaryExtended, event: Event): void {
    this.menuItems = [];

    // Azione: Invia schede
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.generic.SEND),
      icon: "assets/icons/user.svg",
      disabled: !user?.canConfirmAndSendCalibration,
      command: () => {
        this.sendCalibrationCards(user);
      }
    });

    // Azione: Accedi alla matrice di calibrazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.COMPARE_TEAM_IN_MATRIX),
      icon: "assets/icons/users-round.svg",
      disabled: !user?.canAccessCalibration,
      command: () => {
        this.onGoToMatrix(user);
      }
    });

    // Azione: Impersona utente
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.IMPERSONATE_USER),
      icon: "assets/icons/user.svg",
      disabled: !user?.canImpersonateUser,
      command: () => {
        this.onImpersonateUser(user);
      }
    });

    // Azione: Impersona manager
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.impersonate.IMPERSONATE_MANAGER),
      icon: "assets/icons/users.svg",
      disabled: !user?.canImpersonateManager,
      command: () => {
        this.onImpersonateManager(user);
      }
    });

    // Azione: Scarica scheda di valutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.DOWNLOAD_EVALUATION_CARD),
      icon: "assets/icons/download.svg",
      disabled: !(user?.evaluationStatus === "COMPLETED" || user?.selfAssessmentStatus === "COMPLETED"),
      command: () => {
        this.downloadEvaluationCard(user);
      }
    });

    // Azione: Reapri autovalutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_SELF_EVALUATION),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenSelfEvaluation,
      command: () => {
        this.reopenSelfEvaluation(user);
      }
    });

    // Azione: Reapri valutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.REOPEN_EVALUATION),
      icon: "assets/icons/wand.svg",
      disabled: !user?.canReopenEvaluation,
      command: () => {
        this.reopenEvaluation(user);
      }
    });

    // Azione: Sollecito autovalutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.SOLICIT_SELF_EVALUATION),
      icon: "assets/icons/mail.svg",
      disabled: !user?.canSolicitSelfEvaluation,
      command: () => {
        this.solicitSelfEvaluation(user);
      }
    });

    // Azione: Sollecito valutazione
    this.menuItems.push({
      label: this.translateService.instant(TRANSLATION_KEYS.admin.monitoring.details.SOLICIT_EVALUATION),
      icon: "assets/icons/mail.svg",
      disabled: !user?.canSolicitEvaluation,
      command: () => {
        this.solicitEvaluation(user);
      }
    });

    this.apeMenu.toggle(event);
  }

  userCheckedChange(user: MonitoringUserSummaryExtended): void {
    const userSelected = this.allUsersData.find(p => p.userId === user.userId);
    if (userSelected) {
      userSelected.checked = !userSelected.checked;
      const ids = this.allUsersData
        .filter(p => p.checked)
        .map(p => p.userId);
      sessionStorage.setItem("userIdsChecked", ids.join(","));
    }
  }

  /**
     * Naviga alla matrice di calibrazione per l'utente selezionato
     * @param user L'utente selezionato
     */
  onGoToMatrix(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    void this.router.navigate([
      "/admin/calibration", this.processId, user.userId
    ]);
  }

  /**
   * Emette l'evento per impersonare l'utente
   * @param user L'utente da impersonare
   */
  private onImpersonateUser(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.impersonateUser.emit(user);
  }

  /**
   * Emette l'evento per impersonare il manager
   * @param user L'utente il cui manager deve essere impersonato
   */
  private onImpersonateManager(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.impersonateManager.emit(user);
  }

  /**
   * Scarica la scheda di valutazione completa
   * @param user L'utente il cui la scheda di valutazione deve essere scaricata
   */
  private downloadEvaluationCard(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.onDownloadEvaluationCard.emit(user);
  }

  /**
   * Invia le schede di calibrazione
   * @param user L'utente il cui le schede di calibrazione devono essere inviate
   */
  private sendCalibrationCards(user: MonitoringUserSummaryExtended): void {
    if (!user) return;
    this.onSendCalibrationCards.emit(user);
  }
}
